import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('polling'); // polling | success | failed | expired
  const [pollCount, setPollCount] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      setPollCount(attempts);

      try {
        const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        const paymentStatus = response.data.payment_status;

        if (paymentStatus === 'paid') {
          setStatus('success');
          return;
        }
        if (paymentStatus === 'failed' || paymentStatus === 'expired') {
          setStatus(paymentStatus);
          return;
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`Payment status poll ${attempts} failed:`, error?.message);
        }
      }

      if (attempts >= MAX_POLLS) {
        setStatus('failed');
        return;
      }

      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchParams]);

  if (status === 'polling') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0] px-4" data-testid="payment-polling">
        <Card className="max-w-md w-full bg-white border border-[#D1D1CD] rounded-lg shadow-sm">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 text-[#2E4632] animate-spin mx-auto mb-4" />
            <h1
              className="text-2xl font-bold text-[#1C1C1E]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Confirming payment...
            </h1>
            <p className="text-[#5C5C5C] mt-2">
              Please wait while we confirm your subscription.
            </p>
            <p className="text-xs text-[#5C5C5C]/70 mt-4">
              Attempt {pollCount} of {MAX_POLLS}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0] px-4" data-testid="payment-success">
        <Card className="max-w-md w-full bg-white border border-[#D1D1CD] rounded-lg shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-[#2E4632]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-[#2E4632]" />
            </div>
            <h1
              className="text-3xl font-bold text-[#1C1C1E]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              You&apos;re all set!
            </h1>
            <p className="text-[#5C5C5C] mt-2 mb-6">
              Your subscription is now active. Welcome to FarmWorker Pro!
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-btn"
                className="w-full h-12 bg-[#2E4632] hover:bg-[#1F3122] text-white font-semibold"
              >
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="w-full h-12 border-[#D1D1CD]"
              >
                View subscription
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // failed or expired
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0] px-4" data-testid="payment-failed">
      <Card className="max-w-md w-full bg-white border border-[#D1D1CD] rounded-lg shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#BC4749]/10 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-[#BC4749]" />
          </div>
          <h1
            className="text-3xl font-bold text-[#1C1C1E]"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Payment {status === 'expired' ? 'expired' : 'not confirmed'}
          </h1>
          <p className="text-[#5C5C5C] mt-2 mb-6">
            {status === 'expired'
              ? 'This checkout session has expired. Please try again.'
              : "We couldn't confirm your payment. If you were charged, please contact support."}
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            className="w-full h-12 bg-[#2E4632] hover:bg-[#1F3122] text-white font-semibold"
          >
            Back to Pricing
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
